import Session from '@/handler/session'
import Menu from '@/components/containers/menu'
import mySpaceShortcuts from '@/handler/mySpaceShortcuts'
import Helper from '@/handler/helper'
import serviceFind from '@/components/inc/find/services'
import FindConfig from '@/components/inc/find/findConfig'
import servicePHP from '@/components/modules/users/php/staff/services'
import ViewsRegistry from '@/router/viewsRegistry'
import actions from './actions'

/*
 * Motor completo de la paleta de comandos. Resuelve en memoria las vistas,
 * acciones y accesos directos, y consulta al backend cuando se buscan datos
 * concretos del sistema.
 */

// Sinónimos: lo que tipea el usuario -> término que existe en el menú.
const SYNONYMS = {
  'plata': 'finanzas',
  'dinero': 'finanzas',
  'factura': 'ventas',
  'facturar': 'ventas',
  'cobrar': 'recibos',
  'cobranza': 'recibos',
  'stock': 'depositos',
  'deposito': 'depositos',
  'agenda': 'recordatorios',
  'aviso': 'recordatorios',
  'pendiente': 'tareas',
  'todo': 'tareas',
  'chat': 'mensajeria',
  'mensaje': 'mensajeria',
}

function normalize(str) {
  if (!str) return ''
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

function applySynonyms(query) {
  var words = query.split(/\s+/).map(word => SYNONYMS[word] || word)
  return words.join(' ')
}

// m_modules.title viene con un prefijo numérico para ordenar (ej.
// "1.Recordatorios") — es el mismo criterio que ya aplicaba menu.js sobre el
// nombre de los hijos del nav; acá se replica para leer ese campo directo de
// rawMenu, sin pasar por el nav.
function stripOrderPrefix(str) {
  return str ? str.substring(str.indexOf('.') + 1) : str
}

// personalTo (ver viewsRegistry.js): la vista lee datos propios del staff
// logueado (ej. Helper.getSeller().id) sin resguardo — si no tiene ese
// atributo personal, la vista queda rota (no revienta, pero no sirve para
// nada). Un chequeo por clave — hoy solo existe 'seller'; si mañana aparece
// un caso real de 'employee' (hoy no lo hay, ver conversación), se agrega acá.
const PERSONAL_TO_CHECK = {
  seller: () => !!Helper.getSeller(),
}
function passesPersonalCheck(entry) {
  if (!entry.personalTo) return true
  var check = PERSONAL_TO_CHECK[entry.personalTo]
  return check ? check() : true
}

// Vistas finas por permiso (m_modules_permissions / permissions.items): acá
// sí existe init (a diferencia del nav, que es un registro por módulo, no
// por vista) y ya vienen título/subtítulo/ícono resueltos. Solo se puede
// armar una entrada navegable para los módulos que ya migraron su
// reference -> ruta a su router.js (ver router/viewsRegistry.js) — los que
// no, quedan afuera acá y se cubren con el fallback de collectModuleViews()
// (su dashboard, como antes de esto).
//
// Las vistas con requiresParam=true (ver viewsRegistry.js) tampoco se
// devuelven acá — necesitan un ID que solo tiene sentido viniendo de otra
// vista (ej. detalle de horas de UN empleado puntual), así que romperían si
// aparecieran sueltas como resultado de búsqueda. Quedan identificadas en
// el registry para cuando se conviertan en acciones puntuales. Tampoco se
// devuelven las personalTo (ver arriba) si el usuario logueado no tiene ese
// atributo.
//
// wantInit=false (default): las vistas normales, sin dashboards — es lo que
// alimenta la búsqueda de todos los días. wantInit=true: solo los
// dashboards — se usa para el comando "#" (ver buildDashboardIndex), que es
// la única forma explícita de pedir el dashboard de un módulo.
function collectPermissionViews(rawMenu, wantInit) {
  var session = Session.getSession()
  var items = (session.auth && session.auth.user.permissions && session.auth.user.permissions.items) || []

  var views = []

  items.forEach(item => {
    // Las vistas sin soporte siguen existiendo para compatibilidad, pero no
    // deben ofrecerse como destino desde la paleta.
    if (item.deprecated) return
    if (Boolean(item.init) !== Boolean(wantInit)) return

    var module = rawMenu[item.module_id]
    if (!module) return

    var moduleViews = ViewsRegistry[module.router]
    if (!moduleViews) return

    var entry = moduleViews[item.reference]
    if (!entry || entry.requiresParam) return
    if (!passesPersonalCheck(entry)) return

    views.push({
      type: wantInit ? 'dashboard' : 'view',
      title: item.title || stripOrderPrefix(module.title),
      subtitle: item.subtitle || '',
      // La paleta muestra el módulo dueño de la vista, no el grupo amplio
      // que puede agrupar módulos distintos (por ejemplo, Precios y Costos).
      group: stripOrderPrefix(module.title) || '',
      icon: item.icon || module.icon || 'fa fa-circle-o',
      to: { name: entry.route },
    })
  })

  return views
}

// Fallback: un ítem por módulo (su dashboard), leído directo de rawMenu
// (permissions.menu — module_id -> m_modules), sin pasar por el nav del
// sidebar. Cubre los módulos que collectPermissionViews() no resuelve
// todavía (no migraron su VIEWS a router.js) — skipModuleRouters excluye
// los que sí, para no duplicar su dashboard como entrada aparte.
function collectModuleViews(rawMenu, skipModuleRouters) {
  var helperRouter = Helper.getIdentifyRouter()
  var views = []

  Object.keys(rawMenu).forEach(moduleId => {
    var module = rawMenu[moduleId]
    if (!module.router || skipModuleRouters.indexOf(module.router) !== -1) return

    views.push({
      // 'view', no 'dashboard': este fallback también alimenta buildIndex()
      // (búsqueda normal, sin "#") para módulos no migrados — si se
      // etiquetara 'dashboard' desaparecerían de esa búsqueda (sections()
      // solo filtra 'view'/'interface' ahí). Como resultado de "#" queda
      // etiquetado distinto a collectPermissionViews(..., true), pero es un
      // caso transitorio (módulos sin migrar).
      type: 'view',
      title: stripOrderPrefix(module.title),
      group: stripOrderPrefix(module.title) || '',
      url: helperRouter + module.router,
      icon: module.icon || 'fa fa-circle-o',
    })
  })

  return views
}

// "Recientes" (ver CommandPalette.vue) es un mecanismo aparte — guarda
// {título, fullPath} de cada navegación en localStorage, sin pasar por
// collectPermissionViews ni por requiresParam/hiddenParamRefs. Si el usuario
// visitó alguna vez una vista de parámetro oculto (ej. viewPHPCustom con el
// id correcto), fullPath no lo refleja (el id nunca estuvo en la URL) y
// queda guardada como si fuera navegable sin contexto — rota para siempre.
// Este helper le permite a "Recientes" preguntarle al mismo registry si una
// ruta, por nombre, requiere parámetro — para no ofrecerla ni guardarla.
function routeRequiresParam(routeName) {
  if (!routeName) return false

  for (var moduleKey in ViewsRegistry) {
    var moduleViews = ViewsRegistry[moduleKey]
    for (var reference in moduleViews) {
      if (moduleViews[reference].route === routeName) {
        return moduleViews[reference].requiresParam
      }
    }
  }

  return false
}

// "Recientes" tampoco debe listar dashboards de módulo -- igual que en la
// búsqueda con texto (collectPermissionViews sin wantInit ya los excluye),
// esos solo se buscan con el comando "#" (ver matchDashboardCommand). Como
// "Recientes" viene de localStorage y no pasa por collectPermissionViews,
// hace falta este chequeo aparte para no grabarlos/mostrarlos ahí. Mismo
// criterio que buildDashboardIndex(): item.init === true en
// permissions.items, resuelto a su nombre de ruta vía ViewsRegistry. El
// dashboard global ("Tableros", DashboardStaff) no pasa por acá -- no tiene
// fila en permissions.items, así que nunca matchea y sigue apareciendo
// como una vista normal, que es lo que ya hace hoy.
function isDashboardRoute(routeName) {
  if (!routeName) return false

  var rawMenu = getRawMenu()
  return collectPermissionViews(rawMenu, true).some(entry => entry.to && entry.to.name === routeName)
}

// "Recientes" se guarda en localStorage y no pasa por el índice al momento
// de mostrarlo. Resolver la ruta contra los permisos actuales evita que una
// vista que luego fue deprecada siga apareciendo allí.
function isDeprecatedRoute(routeName) {
  if (!routeName) return false

  var session = Session.getSession()
  var items = (session.auth && session.auth.user.permissions && session.auth.user.permissions.items) || []
  var rawMenu = getRawMenu()

  return items.some(item => {
    if (!item.deprecated) return false

    var module = rawMenu[item.module_id]
    var entry = module && ViewsRegistry[module.router] && ViewsRegistry[module.router][item.reference]
    return entry && entry.route === routeName
  })
}

// Busca el ícono de un path dentro del nav (mismo criterio que
// DefaultContainer.findNavIconByPath): match exacto o por prefijo.
function findIconByPath(nav, path) {
  if (!path) return null

  var found = null
  var walk = (nodes) => {
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i]
      if (node.url && (node.url === path || path.indexOf(node.url) === 0)) {
        found = node.icon || found
      }
      if (node.children && node.children.length) {
        walk(node.children)
      }
    }
  }
  walk(nav)

  return found
}

function hasModuleAccess(menu, moduleRouter) {
  if (!moduleRouter) return true
  if (!menu) return false

  for (const prop in menu) {
    if (menu[prop].router == moduleRouter) return true
  }
  return false
}

function getRawMenu() {
  var session = Session.getSession()
  return (session.auth && session.auth.user.permissions && session.auth.user.permissions.menu) || {}
}

function getNav() {
  var rawMenu = getRawMenu()
  if (!Object.keys(rawMenu).length) return []
  return Menu.getItems(rawMenu)
}

// module_id de cada módulo accesible (claves de rawMenu, ya únicas) — para
// consultar sus interfaces PHP.
function collectModuleIds(rawMenu) {
  return Object.keys(rawMenu).map(Number)
}

// Interfaces PHP: pantallas dinámicas cargadas por módulo (ver
// SimpleModuleDashboard.vue / ServicesPHP.obtenerPHPByModule). Solo se
// consultan si el usuario tiene el permiso general de PHP custom, y solo
// para los módulos a los que ya tiene acceso — un request en paralelo por
// módulo. Es async porque depende del backend, así que se resuelve aparte
// de buildIndex() y se mergea al índice una vez que responde (ver
// CommandPalette.vue).
function loadInterfaces() {
  if (!Helper.showAccessPHPCustom()) return Promise.resolve([])

  var moduleIds = collectModuleIds(getRawMenu())
  if (!moduleIds.length) return Promise.resolve([])

  return Promise.all(moduleIds.map(moduleId =>
    servicePHP.obtenerPHPByModule(moduleId)
      .then(response => (response.data || [])
        .map(item => ({
          type: 'interface',
          title: item.name,
          group: 'Interfaz',
          subtitle: item.observations || '',
          icon: 'fa fa-code',
          to: { name: 'ViewStaffViewPHPCustom', params: { id: String(item.id) } },
        })))
      .catch(() => [])
  )).then(results => indexEntries(results.reduce((acc, items) => acc.concat(items), [])))
}

// Accesos directos: siempre son los de Mi Espacio (mySpaceShortcuts.js), no
// los Favoritos globales (shortcuts.js, ver DefaultTabsLayout/aside) — son
// dos listas con propósitos distintos, esta solo sirve al comando "/" (ver
// buildShortcutIndex más abajo). No se mezclan con la búsqueda normal ni con
// el estado vacío de la paleta (ver sections() en CommandPalette.vue).
function collectShortcuts(nav) {
  var entries = []

  mySpaceShortcuts.all().forEach(item => {
    if (item.type === 'divider') return
    entries.push({
      type: 'shortcut',
      title: item.title,
      group: 'Acceso directo',
      url: item.fullPath,
      icon: findIconByPath(nav, item.fullPath) || 'fa fa-bookmark',
    })
  })

  return entries
}

// Las acciones de actions.js, filtradas por acceso al módulo — lo usa
// buildIndex() (mezcladas con todo lo demás) y buildActionIndex() (el
// comando "@", ver más abajo), para no duplicar el filtro en dos lugares.
function collectActions(menu) {
  var entries = []

  actions().forEach(action => {
    if (!hasModuleAccess(menu, action.moduleRouter)) return
    if (action.available && !action.available()) return
    entries.push({
      type: 'action',
      title: action.label,
      group: 'Acción',
      keywords: (action.keywords || []).join(' '),
      icon: action.icon,
      to: action.to,
    })
  })

  return entries
}

// Las acciones puntuales (actions.js) NO están acá — solo se buscan con el
// comando "@" (ver buildActionIndex), para no competir con vistas/
// interfaces en el resultado normal. Accesos directos tampoco: solo salen
// con "/" (ver collectShortcuts/buildShortcutIndex), para no competir en la
// búsqueda de texto libre ni en el estado vacío.
function buildIndex() {
  var rawMenu = getRawMenu()

  var entries = []

  // "Tableros" (el panel de widgets, DashboardStaff) no tiene fila propia en
  // permissions.items — no es un módulo real, es la pantalla base de
  // cualquier staff — así que no sale de collectPermissionViews ni de
  // collectModuleViews (esas dos leen rawMenu). Se agrega a mano para que
  // siga apareciendo como resultado de vista normal.
  entries.push({
    type: 'view',
    title: 'Tableros',
    group: '',
    icon: 'dashboard',
    to: { name: 'DashboardStaff' },
  })

  var permissionViews = collectPermissionViews(rawMenu)
  permissionViews.forEach(view => { entries.push(view) })

  var migratedModuleRouters = Object.keys(ViewsRegistry)
  collectModuleViews(rawMenu, migratedModuleRouters).forEach(view => { entries.push(view) })

  return indexEntries(entries)
}

/*
 * ATAJOS DE TECLADO DEL BUSCADOR — documentados acá porque no hay otro
 * lugar donde vivan (no se pueden descubrir solos, hay que saberlos de
 * memoria). Ver CommandPalette.vue (dashboardCommandQuery/actionCommandQuery/
 * shortcutCommandQuery en computed) para cómo se consumen — la query
 * completa se reemplaza por la búsqueda dentro del índice correspondiente,
 * no se mezcla con lo demás.
 *
 *   #<módulo>   Lista SOLO dashboards de módulo (ej. "#presupuesto"). Es la
 *               única forma de llegar al dashboard de un módulo desde el
 *               buscador — las búsquedas normales lo excluyen a propósito
 *               (ver collectPermissionViews/wantInit).
 *   @<acción>   Lista SOLO acciones puntuales de actions.js
 *               (ej. "@recordatorio"), sin que compitan con vistas/
 *               interfaces en el resultado.
 *   /<término>  Lista SOLO accesos directos (shortcuts.js), mismo criterio
 *               que "#"/"@" — la búsqueda con texto normal no los incluye
 *               (ver CommandPalette.vue/sections). Siguen apareciendo en el
 *               estado vacío del buscador (sin query), donde no compiten
 *               con nada más.
 *
 * Los tres, sin texto después del símbolo (solo "#", "@" o "/"), muestran la
 * lista completa sin filtrar.
 */

// Índice aparte, solo dashboards — nunca se mezcla con buildIndex() a
// propósito (por eso las vistas normales excluyen init). Lo consume "#".
function buildDashboardIndex() {
  var rawMenu = getRawMenu()
  var entries = collectPermissionViews(rawMenu, true)

  var migratedModuleRouters = Object.keys(ViewsRegistry)
  entries = entries.concat(collectModuleViews(rawMenu, migratedModuleRouters))

  return indexEntries(entries)
}

const DASHBOARD_COMMAND = /^#\s*(.*)$/
function matchDashboardCommand(query) {
  var match = String(query || '').trim().match(DASHBOARD_COMMAND)
  return match ? match[1] : null
}

// Índice aparte, solo acciones puntuales de actions.js. Lo consume "@".
function buildActionIndex() {
  var rawMenu = getRawMenu()
  var menu = Object.keys(rawMenu).length ? rawMenu : null
  return indexEntries(collectActions(menu))
}

const ACTION_COMMAND = /^@\s*(.*)$/
function matchActionCommand(query) {
  var match = String(query || '').trim().match(ACTION_COMMAND)
  return match ? match[1] : null
}

// Índice aparte, solo accesos directos (siempre los de Mi Espacio). Lo
// consume "/".
function buildShortcutIndex() {
  return indexEntries(collectShortcuts(getNav()))
}

const SHORTCUT_COMMAND = /^\/\s*(.*)$/
function matchShortcutCommand(query) {
  var match = String(query || '').trim().match(SHORTCUT_COMMAND)
  return match ? match[1] : null
}

// Texto normalizado precalculado para no repetirlo en cada tecla — se usa
// tanto en buildIndex() (síncrono) como al mergear resultados que llegan
// después de forma async (ver loadInterfaces()).
function indexEntries(entries) {
  entries.forEach(entry => {
    entry._title = normalize(entry.title)
    entry._group = normalize(entry.group)
    entry._keywords = normalize(entry.keywords || '')
    entry._subtitle = normalize(entry.subtitle || '')
  })

  return entries
}

// Scoring: prefijo de palabra en el título > substring en el título >
// match en keywords > match en el grupo > match en el subtítulo. Todas las
// palabras de la query deben matchear en algún campo (AND).
function scoreEntry(entry, words) {
  var total = 0

  for (var i = 0; i < words.length; i++) {
    var word = words[i]
    var wordScore = 0

    if (entry._title.indexOf(word) === 0) {
      wordScore = 100
    } else if (entry._title.indexOf(' ' + word) !== -1) {
      wordScore = 80
    } else if (entry._title.indexOf(word) !== -1) {
      wordScore = 60
    } else if (entry._keywords && entry._keywords.indexOf(word) !== -1) {
      wordScore = 40
    } else if (entry._group && entry._group.indexOf(word) !== -1) {
      wordScore = 20
    } else if (entry._subtitle && entry._subtitle.indexOf(word) !== -1) {
      wordScore = 10
    }

    if (!wordScore) return 0
    total += wordScore
  }

  return total
}

function search(query, index) {
  var normalized = applySynonyms(normalize(query))
  if (!normalized) return []

  var words = normalized.split(/\s+/)

  return index
    .map(entry => ({ entry: entry, score: scoreEntry(entry, words) }))
    .filter(result => result.score > 0)
    .sort((a, b) => b.score - a.score)
    .map(result => result.entry)
}

/*
 * Búsqueda asíncrona de registros. Se mantiene dentro del mismo módulo de la
 * paleta, pero separada de la búsqueda local para que CommandPalette pueda
 * aplicar debounce y mostrar ambas clases de resultado de forma independiente.
 */
const RESULTS_PER_TYPE = 6

function hasActiveDataModule(moduleId) {
  var session = Session.getSession()
  var modules = session.auth && session.auth.user && session.auth.user.permissions && session.auth.user.permissions.modules
  if (!Array.isArray(modules)) return false

  return modules.some(module => {
    if (module.id != moduleId) return false
    return module.active === true || module.active === 1 || module.active === '1' || module.active === 'true'
  })
}

function hasDataAccess(entry) {
  // Un vendedor trabaja sobre su cartera mediante los flujos propios de
  // ventas; la paleta no debe exponer el directorio global de clientes ni
  // proveedores, aunque tenga alguna vista heredada en la sesión.
  if (Helper.getSeller()) return false

  // Primero el módulo debe estar activo. Luego se exige la vista dueña
  // explícita: Clientes y Proveedores requieren `crud`; Proyectos,
  // `crudProject`. Un permiso de módulo genérico o una vista relacionada no
  // habilitan a consultar el directorio completo.
  if (!entry || !entry.permission || !entry.permission.view_reference) return false
  if (!hasActiveDataModule(entry.permission.module_id)) return false

  var references = entry.permission.required_view_references || [entry.permission.view_reference]
  return references.every(reference => Helper.showAccessView(entry.permission, reference))
}

function plainText(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function dataRelevance(item, term) {
  var query = normalize(term)
  var words = query.split(/\s+/).filter(Boolean)
  var title = normalize(item.name)
  var code = normalize(item.code)
  var notes = normalize(plainText(item.notes))
  var titleHasAllWords = words.length && words.every(word => title.indexOf(word) !== -1)
  var notesHasAllWords = words.length && words.every(word => notes.indexOf(word) !== -1)
  var score = 0

  // El identificador principal siempre manda sobre una coincidencia dentro
  // de una nota; las notas quedan como una segunda vía precisa para datos
  // particulares que no forman parte del nombre.
  if (title === query) score += 1200
  else if (title.indexOf(query) === 0) score += 900
  else if (titleHasAllWords) score += 700
  else if (title.indexOf(query) !== -1) score += 550

  if (code === query) score += 1000
  else if (code.indexOf(query) !== -1) score += 500

  if (notes.indexOf(query) !== -1) score += 420
  else if (notesHasAllWords) score += 300

  return { score: score, matchedNotes: notesHasAllWords }
}

function searchGenericData(term, configKey, group, icon, toBuilder, subtitleBuilder) {
  var entry = FindConfig[configKey]
  if (!hasDataAccess(entry)) return Promise.resolve([])

  return serviceFind.filter(term, [], entry, [], { include_notes: true })
    .then(response => (response.data || []).map(item => {
      var relevance = dataRelevance(item, term)
      var baseSubtitle = subtitleBuilder ? subtitleBuilder(item) : (item.code || '')
      return {
        type: 'data',
        group: group,
        icon: icon,
        title: item.name,
        subtitle: baseSubtitle,
        matchesNotes: relevance.matchedNotes,
        to: toBuilder(item),
        _score: relevance.score,
      }
    }).sort((a, b) => b._score - a._score).slice(0, RESULTS_PER_TYPE))
    .catch(() => [])
}

function searchCustomers(term) {
  return searchGenericData(term, 'customers', 'Clientes', 'fa fa-address-book',
    item => ({ name: 'CustomerViewStaff', params: { customersID: String(item.id) } }))
}

function searchSuppliers(term) {
  return searchGenericData(term, 'suppliers', 'Proveedores', 'fa fa-truck',
    item => ({ name: 'SuppliersViewStaff', params: { suppliersID: String(item.id) } }))
}

function searchProjects(term) {
  return searchGenericData(term, 'project', 'Proyectos', 'folder',
    item => ({
      name: 'ProjectStaffCrudProject',
      query: {
        project_id: String(item.id),
        project_search: String(Date.now()),
      },
    }),
    item => item.customer && item.customer.name ? item.customer.name : '')
}

// Staff y Productos quedan afuera por ahora (a pedido).
function searchData(term) {
  return Promise.all([
    searchCustomers(term),
    searchSuppliers(term),
    searchProjects(term),
  ]).then(results => results.reduce((acc, items) => acc.concat(items), []))
}

export default {
  buildIndex,
  buildDashboardIndex,
  matchDashboardCommand,
  buildActionIndex,
  matchActionCommand,
  buildShortcutIndex,
  matchShortcutCommand,
  search,
  searchData,
  loadInterfaces,
  normalize,
  indexEntries,
  getNav,
  findIconByPath,
  routeRequiresParam,
  isDashboardRoute,
  isDeprecatedRoute,
}
